import { RequestProps, User } from '../../../store/types'
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import FilterMenu from '../FilterMenu'
import { isEqual } from 'lodash'
import CollapsibleSection from '../../Button/CollapsibleSection'
import { PermissionLight, useGetPermissionsQuery } from '../../../store/redux/reducers/permissionsSlice'
import defaultProps from '../../../config/defaultProps'
import { useGetRolesQuery } from '../../../store/redux/reducers/rolesSlice'
import { Checkbox, FormControlLabel } from '@mui/material'
import { useGetLightUsersQuery } from '../../../store/redux/reducers/usersSlice'
import PermissionInfiniteScroll from './PermissionInfiniteScroll'
import UserInfiniteScroll from './UserInfiniteScroll'
import { useLeftPadding } from '../../../store/redux/hooks'

export type FilterType = {
  roleNames: string[]
  permissions: string[]
  employees: string[]
}

export const initialFilters: FilterType = {
  roleNames: [],
  permissions: [],
  employees: [],
}

const FilterRolesMenu = ({
  requestProps,
  setRequestProps,
  setPage,
}: {
  requestProps: RequestProps
  setRequestProps: React.Dispatch<React.SetStateAction<RequestProps>>
  setPage: Dispatch<SetStateAction<number>>
}) => {
  const { t } = useTranslation()
  const leftPadding = useLeftPadding()
  const [selectedFilters, setSelectedFilters] = useState<FilterType>(initialFilters)
  const [displayedPermissions, setDisplayedPermissions] = useState<PermissionLight[]>([])
  const [hasMorePermissions, setHasMorePermissions] = useState<boolean>(true)
  const [permissionRequestProps, setPermissionRequestProps] = useState<RequestProps>({ page: 0, size: 10 })
  const [displayedUsers, setDisplayedUsers] = useState<User[]>([])
  const [hasMoreUsers, setHasMoreUsers] = useState<boolean>(true)
  const [userRequestProps, setUserRequestProps] = useState<RequestProps>({ page: 0, size: 10 })

  const { data: roles } = useGetRolesQuery(defaultProps)
  const { data: permissions } = useGetPermissionsQuery(permissionRequestProps)
  const { data: users } = useGetLightUsersQuery(userRequestProps)

  const handleFetchMorePermissions = () => {
    setPermissionRequestProps(prev => ({ ...prev, page: prev.page + 1 }))
  }

  useEffect(() => {
    if (permissions) {
      setHasMorePermissions(!permissions.last)
      setDisplayedPermissions(prev => [...prev].concat(permissions.content))
    }
  }, [permissions])

  const handleFetchMoreUsers = () => {
    setUserRequestProps(prev => ({ ...prev, page: prev.page + 1 }))
  }

  useEffect(() => {
    if (users) {
      setHasMoreUsers(!users.last)
      setDisplayedUsers(prev => [...prev].concat(users.content))
    }
  }, [users])

  const handleRoleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value
    let isChecked = event.target.checked
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      roleNames: isChecked ? [...prevFilters.roleNames, value] : prevFilters.roleNames.filter(e => e !== value),
    }))
  }

  const handlePermissionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value
    let isChecked = event.target.checked
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      permissions: isChecked ? [...prevFilters.permissions, value] : prevFilters.permissions.filter(e => e !== value),
    }))
  }

  const handleUserChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value
    let isChecked = event.target.checked
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      employees: isChecked ? [...prevFilters.employees, value] : prevFilters.employees.filter(e => e !== value),
    }))
  }

  const handleFiltering = () => {
    let newProps: RequestProps = { ...requestProps, page: 0 }

    newProps = {
      ...newProps,
      name: selectedFilters.roleNames?.length > 0 ? selectedFilters.roleNames.toString() : undefined,
      'permissions.id': selectedFilters.permissions?.length > 0 ? selectedFilters.permissions.toString() : undefined,
      'employees.id': selectedFilters.employees?.length > 0 ? selectedFilters.employees.toString() : undefined,
    }

    if (isEqual(requestProps, newProps)) return

    setRequestProps({ ...newProps })
    setPage(0)
  }

  const handleClear = () => {
    setSelectedFilters(initialFilters)
    setPage(0)
    setRequestProps({ page: 0, size: requestProps.size })
  }

  return (
    <FilterMenu filter={handleFiltering} clear={handleClear}>
      {roles && roles.length > 0 && (
        <CollapsibleSection text={t('page.adminsPage.filtering.roles')}>
          {roles.map(role => (
            <FormControlLabel
              sx={{ pl: leftPadding }}
              control={<Checkbox checked={selectedFilters.roleNames.includes(role.name)} onChange={handleRoleChange} />}
              label={t(`roles.${role.name}`)}
              value={role.name}
              key={`${role.id}-role-filtering-menu-item`}
            />
          ))}
        </CollapsibleSection>
      )}
      {permissions && (
        <PermissionInfiniteScroll
          displayedPermissions={displayedPermissions}
          hasMore={hasMorePermissions}
          fetchMore={handleFetchMorePermissions}
          selectedPermissions={selectedFilters.permissions}
          handlePermissionCheck={handlePermissionChange}
        />
      )}
      {users && (
        <UserInfiniteScroll
          displayedUsers={displayedUsers}
          hasMore={hasMoreUsers}
          fetchMore={handleFetchMoreUsers}
          selectedUsers={selectedFilters.employees}
          handleUserCheck={handleUserChange}
        />
      )}
    </FilterMenu>
  )
}

export default FilterRolesMenu
